import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import useLoad from '../../eventsPublic/functions/useLoad'
import { loadJobs } from '../../eventsPublic/apis/publicApi'
import { LABELS, JOB_TYPES } from '../constants/staticText'
import { useDocumentTitle } from '../../lib/a11y/useDocumentTitle'
import JobsTable from './JobsTable'
import JobsPager, { SEITE } from './JobsPager'
import EventBreadcrumb from '../../eventsPublic/components/EventBreadcrumb'
import jobsTrail from '../functions/jobsTrail'
import LoadingIndicator from '../../lib/components/LoadingIndicator'
import useDebouncedValue from '../../lib/functions/useDebouncedValue'

// Wartezeit, nach der ein geaenderter Suchbegriff abgefragt wird: lang genug,
// dass nicht jede Taste den Server fragt, kurz genug, dass es als "sofort" wirkt.
const SUCH_WARTEZEIT_MS = 300
// So lange steht das Lade-Logo bei einer neuen Suche mindestens: etwa ein
// Zeichenzug des Schwungs. Der Server antwortet sonst, bevor es zu sehen ist.
const SUCH_LADEZEIT_MS = 800

// Aufbau wie auf der Live-Seite: Suchfeld mit Beschriftung, ein Schalter je
// Angebotsart, Trefferzahl und darunter eine Liste — Logo links, Titel und
// Firma in der Mitte, Art rechts in Versalien.
const TypeSwitch = ({ label, checked, onChange }) => (
    <label className="jobs-switch">
        <input type="checkbox" checked={checked} onChange={onChange} />
        <span className="jobs-switch-track"><span className="jobs-switch-knob" /></span>
        <span className="jobs-switch-label">{label}</span>
    </label>
)

const JobsList = () => {
    // Die Suche laeuft mit: Schalter wirken sofort, der Suchbegriff, sobald die
    // Eingabe kurz ruht (vorher wendete erst "Suchen" beides an, #212).
    // Die Ausstellerkarte einer Messe verlinkt hierher mit ?company=<id>. Die
    // Einschraenkung gehoert in die Adresse, damit der Verweis teilbar bleibt (#53).
    const [searchParams, setSearchParams] = useSearchParams()
    // Die WordPress-Suche (#238) verlinkt mit ?suche=<Begriff>: die Liste
    // beginnt mit diesem Begriff, im Suchfeld und in der Abfrage.
    const startbegriff = searchParams.get('suche') || ''
    const [types, setTypes] = useState([])
    const [draft, setDraft] = useState(startbegriff)
    const [query, setQuery] = useState(startbegriff)
    const ruhigerEntwurf = useDebouncedValue(draft, SUCH_WARTEZEIT_MS)
    const company = searchParams.get('company')
    // Die Seite steht in der Adresse (#223): teilbar, und Suchmaschinen
    // erreichen alle Anzeigen. Seite 1 traegt keinen Parameter. Der Name steht
    // in JobsPager (nicht page — den nimmt WordPress, siehe dort).
    const page = Math.max(1, parseInt(searchParams.get(SEITE), 10) || 1)

    const params = `?${new URLSearchParams({
        ...(types.length && { type: types.join(',') }),
        ...(query && { q: query }),
        ...(company && { company }),
        ...(page > 1 && { page: String(page) })
    })}`
    const { data, loading, reloading, error } = useLoad(
        () => loadJobs(params),
        [types.join(','), query, company, page],
        { minReloadMs: SUCH_LADEZEIT_MS }
    )
    // Titel im Tab: mit Firmenfilter traegt er den Firmennamen (#178).
    useDocumentTitle(data?.companyFilter ? `${LABELS.pageTitle} von ${data.companyFilter.name}` : LABELS.pageTitle)

    // Eine neue Suche beginnt auf Seite 1: Seite 5 eines Filters mit drei
    // Treffern waere leer.
    const seiteZuruecksetzen = () => {
        if (searchParams.has(SEITE)) {
            const ohneSeite = new URLSearchParams(searchParams)
            ohneSeite.delete(SEITE)
            setSearchParams(ohneSeite)
        }
    }

    const suchbegriffAnwenden = (wert) => {
        if (wert === query) return
        setQuery(wert)
        seiteZuruecksetzen()
    }

    useEffect(() => { suchbegriffAnwenden(ruhigerEntwurf) }, [ruhigerEntwurf])

    // Nach einem Klick in der Seitenleiste gehoert der Fokus an den Anfang der
    // Liste: der angeklickte Verweis ist weg, sonst fiele er auf BODY (#236).
    // Ohne Scrollen und ohne Ring — das Aussehen bleibt.
    const ueberschrift = useRef(null)
    const location = useLocation()
    useEffect(() => {
        if (location.state?.seitenwechsel) ueberschrift.current.focus({ preventScroll: true })
    }, [location.key])

    const toggle = (type) => {
        setTypes(types.includes(type) ? types.filter(t => t !== type) : [...types, type])
        seiteZuruecksetzen()
    }

    // Enter im Suchfeld wendet den Begriff ohne Wartezeit an.
    const suchen = (event) => {
        event.preventDefault()
        suchbegriffAnwenden(draft)
    }

    return (
        <>
            {/* Dieselbe Leiste wie in der Events-App, ueber dem Inhalt (#82). */}
            <EventBreadcrumb trail={jobsTrail(null)} />
            <div className="jobs-list">
                {/* Die Hauptueberschrift der Seite (Audit 2026-10-07, S3). Die
                    Live-Vorlage zeigt ueber der Liste keinen Titel — sie steht
                    darum nur fuer Screenreader und Suchmaschinen im Dokument. */}
                <h1 className="jm-sr-only" tabIndex={-1} ref={ueberschrift}>{LABELS.pageTitle}</h1>
                <form
                    className="jobs-search"
                    onSubmit={suchen}
                >
                    <div className="jobs-search-field">
                        <label htmlFor="jobs-query">{LABELS.searchLabel}</label>
                        <input
                            id="jobs-query"
                            type="search"
                            value={draft}
                            placeholder={LABELS.searchPlaceholder}
                            onChange={event => setDraft(event.target.value)}
                        />
                    </div>

                    <div className="jobs-switches">
                        {JOB_TYPES.map(type => (
                            <TypeSwitch
                                key={type}
                                label={type}
                                checked={types.includes(type)}
                                onChange={() => toggle(type)}
                            />
                        ))}
                    </div>

                </form>

                {/* Die Liste benennt ihre Einschraenkung und laesst sie wieder aufheben —
                    sonst waere nicht erkennbar, warum nur ein Teil der Anzeigen erscheint. */}
                {data?.companyFilter && (
                    <p className="jobs-company-filter">
                        <strong>{LABELS.jobsOfCompany} {data.companyFilter.name}</strong>
                        <Link className="jobs-company-filter-clear" to="/stellenangebote/">{LABELS.back}</Link>
                    </p>
                )}

                {loading && <LoadingIndicator label={LABELS.loadingJobs} immediate={reloading} />}
                {error && <p className="jobs-state" role="status">{LABELS.error}</p>}

                {data && (
                    <p className="jobs-count" role="status">
                        <strong>{data.total} {LABELS.countNoun}</strong> {LABELS.countFound}
                    </p>
                )}

                {data && 0 === data.items.length && <p className="jobs-state">{LABELS.empty}</p>}

                <JobsTable jobs={data?.items || []} />

                {data && <JobsPager page={data.page} total={data.total} pageSize={data.pageSize} searchParams={searchParams} />}

            </div>
        </>
    )
}

export default JobsList
